/**
 * Vendor Bot Admin - Referrals Page
 * Configure multi-level referral system (1-5 levels)
 */

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import Navbar from '../../components/Navbar';
import Card from '../../components/Card';
import Button from '../../components/Button';
import { useTelegram } from '../../hooks/useTelegram';

const ConfigSection = styled(Card)`
  margin-bottom: var(--spacing-lg);
`;

const SectionTitle = styled.h3`
  font-size: var(--font-lg);
  font-weight: var(--font-semibold);
  color: var(--text-primary);
  margin-bottom: var(--spacing-md);
`;

const LevelsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
`;

const LevelRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-md);
  background: var(--bg-secondary);
  border-radius: var(--radius-md);
`;

const LevelNumber = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--accent-green);
  color: white;
  border-radius: var(--radius-full);
  font-weight: var(--font-bold);
  font-size: var(--font-md);
  flex-shrink: 0;
`;

const LevelInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
`;

const LevelLabel = styled.div`
  font-size: var(--font-md);
  font-weight: var(--font-medium);
  color: var(--text-primary);
`;

const Input = styled.input`
  width: 100px;
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  color: var(--text-primary);
  font-size: var(--font-md);
  text-align: center;

  &:focus {
    outline: none;
    border-color: var(--accent-green);
  }
`;

const InfoCard = styled(Card)`
  background: rgba(76, 175, 80, 0.1);
  border: 1px solid var(--accent-green);
  margin-bottom: var(--spacing-lg);
`;

const InfoText = styled.p`
  font-size: var(--font-sm);
  color: var(--text-secondary);
  line-height: 1.5;
`;

const AdminReferrals: React.FC = () => {
  const { botUsername } = useParams<{ botUsername: string }>();
  const navigate = useNavigate();
  const { showBackButton, notificationFeedback } = useTelegram();
  const [levels, setLevels] = useState([
    { level: 1, commission: 10, active: true },
    { level: 2, commission: 5, active: true },
    { level: 3, commission: 3, active: true },
    { level: 4, commission: 2, active: false },
    { level: 5, commission: 1, active: false },
  ]);

  useEffect(() => {
    showBackButton(() => navigate(`/vendor/${botUsername}/admin/dashboard`));
  }, [showBackButton, navigate, botUsername]);

  const handleCommissionChange = (levelIndex: number, value: string) => {
    const newLevels = [...levels];
    newLevels[levelIndex].commission = parseFloat(value) || 0;
    setLevels(newLevels);
  };

  const handleSave = () => {
    notificationFeedback('success');
  };

  return (
    <Layout header={<Navbar title="Referral Settings" showBack />}>
      <InfoCard>
        <InfoText>
          Configure commission rates for each referral level. Users earn commission when their referrals make purchases.
          Level 1 = direct referral, Level 2 = referral's referral, etc.
        </InfoText>
      </InfoCard>

      <ConfigSection>
        <SectionTitle>Commission Levels</SectionTitle>
        <LevelsList>
          {levels.map((level, index) => (
            <LevelRow key={level.level}>
              <LevelNumber>{level.level}</LevelNumber>
              <LevelInfo>
                <LevelLabel>Level {level.level} Commission</LevelLabel>
              </LevelInfo>
              <Input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={level.commission}
                onChange={(e) => handleCommissionChange(index, e.target.value)}
              />
              <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--font-md)' }}>%</span>
            </LevelRow>
          ))}
        </LevelsList>
      </ConfigSection>

      <Button fullWidth onClick={handleSave}>
        Save Settings
      </Button>
    </Layout>
  );
};

export default AdminReferrals;
